import { useState } from 'react';
import { T, useGT } from 'gt-react';
import { useRouter } from '@tanstack/react-router';
import { appGallerySpec, gentlyTheme } from '@erp/json-renderer/apps';
import type { AppTheme } from '@erp/json-renderer/apps';
import { AppView } from '@erp/json-renderer/apps/react';
import Page from 'src/components/page/Page';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';

/**
 * A sample brand theme, to show that an App re-skins through token overrides
 * alone. Real brand themes will come from an org's Template Blocks brand kit.
 */
const SAMPLE_BRAND_THEME: AppTheme = {
  id: 'sample-brand',
  label: 'Sample brand',
  light: {
    primary: 'oklch(0.42 0.09 252)',
    'primary-foreground': 'oklch(0.98 0.01 252)',
    ring: 'oklch(0.62 0.09 252)',
    radius: '0.25rem',
  },
  dark: {
    primary: 'oklch(0.78 0.08 252)',
    'primary-foreground': 'oklch(0.2 0.03 252)',
  },
};

const THEMES = { gently: gentlyTheme, 'sample-brand': SAMPLE_BRAND_THEME };
type ThemeId = keyof typeof THEMES;

function isThemeId(value: string): value is ThemeId {
  return value in THEMES;
}

export function AppsGalleryPage() {
  const gt = useGT();
  const router = useRouter();
  const [themeId, setThemeId] = useState<ThemeId>('gently');
  return (
    <Page
      subtitle={
        <T context="Apps gallery page description">
          Every App component, rendered in the selected theme.
        </T>
      }
      actions={
        <Select
          value={themeId}
          onValueChange={(value) => {
            if (isThemeId(value)) setThemeId(value);
          }}
        >
          <SelectTrigger
            className="w-40"
            aria-label={gt('Theme', {
              context: 'Apps gallery theme switcher label',
            })}
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="gently">
              <T context="Apps gallery theme option">Gently</T>
            </SelectItem>
            <SelectItem value="sample-brand">
              <T context="Apps gallery theme option">Sample brand</T>
            </SelectItem>
          </SelectContent>
        </Select>
      }
    >
      <AppView
        spec={appGallerySpec}
        theme={THEMES[themeId]}
        navigate={(to) => router.history.push(to)}
      />
    </Page>
  );
}
